import {
  EditProductRequest,
  ProductDetailsResponse,
  ProductStatus,
} from '@/@types/Products';
import { isAllowedRefundDeadlineDays, normalizeRefundDeadlineDays } from '@/constants/refundDeadline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { isCompleteHttpUrl, isValidHttpUrl, normalizeUrlOnBlur } from '@/utils/normalize';

export function useEditProductSchema(
  t: (key: string) => string,
  product: ProductDetailsResponse
) {
  const productEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const EditProductSchema = z
    .object({
      name: z.string().min(1, t('required')),
      description: z.string().min(10, t('validation:description_min_length')),
      phone_prefix: z.string().min(1, t('required')),
      whatsapp_support: z.string().min(1, t('required')),
      email_support: z.string().min(1, t('required')).email(t('validation:email_invalid')),
      sales_page: z
        .string()
        .min(1, t('required'))
        .refine(
          (value) => isValidHttpUrl(normalizeUrlOnBlur(value)),
          t('validationInvalidSalesPage')
        ),
      thankyou_url: z.string().optional().nullable(),
      image: z.any().optional(),
      status: z.string(),
      category_id: z.string(),
      product_extras: z.string().optional(),
      product_url: z.string(),
      product_email: z.string(),
      product_password: z.string(),
      category_type: z.string().optional(),
      product_delivery: z.string().optional(),
      support_url: z.string().optional().nullable(),
      privacy_terms_url: z.string().optional().nullable(),
      refund_deadline_days: z.coerce
        .number({ invalid_type_error: t('required') }),
    })
    .superRefine((data, ctx) => {
      if (
        data.refund_deadline_days != null &&
        !isAllowedRefundDeadlineDays(data.refund_deadline_days, data.category_type)
      ) {
        ctx.addIssue({
          path: ['refund_deadline_days'],
          code: z.ZodIssueCode.custom,
          message: t('refundDeadlineDaysInvalidOption'),
        })
      }

      // Validação do thankyou_url (opcional)
      const thankRaw = data.thankyou_url?.trim() ?? '';
      if (thankRaw) {
        const normalizedThankyou = normalizeUrlOnBlur(thankRaw);
        if (!isCompleteHttpUrl(normalizedThankyou)) {
          ctx.addIssue({
            path: ['thankyou_url'],
            code: z.ZodIssueCode.custom,
            message: t('common:validationInvalidThankyouUrl'),
          });
        } else if (normalizedThankyou.length > 500) {
          ctx.addIssue({
            path: ['thankyou_url'],
            code: z.ZodIssueCode.custom,
            message: t('common:validationInvalidThankyouUrl'),
          });
        }
      }

      if (
        data.category_type === 'digital' &&
        data.product_delivery === 'no_delivery'
      ) {
        if (!data.product_url) {
          ctx.addIssue({
            path: ['product_url'],
            code: z.ZodIssueCode.custom,
            message: t('required'),
          });
        } else if (!isValidHttpUrl(normalizeUrlOnBlur(data.product_url))) {
          ctx.addIssue({
            path: ['product_url'],
            code: z.ZodIssueCode.custom,
            message: t('validationInvalidSalesPage'),
          });
        }
        if (!data.product_email) {
          ctx.addIssue({
            path: ['product_email'],
            code: z.ZodIssueCode.custom,
            message: t('required'),
          });
        } else if (!productEmailRegex.test(data.product_email)) {
          ctx.addIssue({
            path: ['product_email'],
            code: z.ZodIssueCode.custom,
            message: t('validation:email_invalid'),
          });
        }
        if (!data.product_password) {
          ctx.addIssue({
            path: ['product_password'],
            code: z.ZodIssueCode.custom,
            message: t('required'),
          });
        }
      }
    });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
    reset,
  } = useForm<EditProductRequest>({
    resolver: zodResolver(EditProductSchema),
    defaultValues: {
      name: '',
      description: '',
      phone_prefix: product?.phone_prefix || '55',
      whatsapp_support: '',
      email_support: '',
      sales_page: '',
      thankyou_url: '',
      status: ProductStatus.ACTIVE,
      category_id: '',
      image: null,
      product_extras: '',
      product_url: '',
      product_email: '',
      product_password: '',
      category_type: product?.category.type || '',
      product_delivery: product?.product_delivery || '',
      support_url: '',
      privacy_terms_url: '',
      refund_deadline_days: normalizeRefundDeadlineDays(
        product?.refund_deadline_days,
        product?.category?.type,
      ),
    },
  });

  const formValues = watch();

  return {
    register,
    handleSubmit,
    errors,
    setValue,
    setError,
    clearErrors,
    formValues,
    reset,
    watch,
  };
}
